// src/lib/firestore.ts
// All Firestore reads and writes go through this module.
// Components never import firebase/firestore directly.

import {
  collection, doc, getDoc, getDocs, addDoc, updateDoc, setDoc, deleteDoc, arrayUnion,
  query, where, orderBy, limit, onSnapshot, serverTimestamp,
  Timestamp, QueryConstraint,
} from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "./firebase";
import { Ticket, HelpDeskUser, NotificationPreferences, TicketAttachment, TicketMessage, KBArticle, SyncLog, TicketStatus, Deflection, DeflectionChannel } from "@/types";

// ─── Collection refs ──────────────────────────────────────────────
export const COL = {
  users:    () => collection(db, "users"),
  tickets:  () => collection(db, "tickets"),
  messages: (ticketId: string) => collection(db, "tickets", ticketId, "messages"),
  kb:       () => collection(db, "knowledgeBase"),
  syncLogs: () => collection(db, "syncLogs"),
  config:   () => collection(db, "config"),
  analytics: () => collection(db, "analytics"),
};

// ─── Tickets ─────────────────────────────────────────────────────

export async function createTicket(data: {
  title: string;
  description: string;
  submitterId: string;
  submitterEmail: string;
  submitterName: string;
  origin?: "manual" | "ai-chat";
  originChat?: string;
}): Promise<string> {
  const payload: Record<string, unknown> = {
    ...data,
    status: "open" as TicketStatus,
    category: "",        // filled by triage function
    priority: null,      // filled by triage function
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  // Strip undefined optional fields so we don't write `null`/`undefined` keys
  if (!data.origin) delete payload.origin;
  if (!data.originChat) delete payload.originChat;
  const ref = await addDoc(COL.tickets(), payload);
  return ref.id;
}

export async function updateTicket(
  ticketId: string,
  data: Partial<Omit<Ticket, "id" | "createdAt">>
): Promise<void> {
  await updateDoc(doc(COL.tickets(), ticketId), {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

export async function resolveTicket(ticketId: string): Promise<void> {
  await updateDoc(doc(COL.tickets(), ticketId), {
    status: "resolved" as TicketStatus,
    resolvedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export function subscribeToTickets(
  constraints: QueryConstraint[],
  cb: (tickets: Ticket[]) => void
) {
  const q = query(COL.tickets(), ...constraints);
  return onSnapshot(q,
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() } as Ticket))),
    (err) => {
      // Most common cause: missing composite index. The error message
      // includes a Firebase Console link to auto-create it.
      console.error("subscribeToTickets failed:", err);
      cb([]); // clear stale data so the UI doesn't lie
    }
  );
}

export function subscribeToTicket(ticketId: string, cb: (t: Ticket | null) => void) {
  return onSnapshot(doc(COL.tickets(), ticketId), (snap) => {
    cb(snap.exists() ? ({ id: snap.id, ...snap.data() } as Ticket) : null);
  });
}

export async function getTicket(ticketId: string): Promise<Ticket | null> {
  const snap = await getDoc(doc(COL.tickets(), ticketId));
  return snap.exists() ? ({ id: snap.id, ...snap.data() } as Ticket) : null;
}

// ─── Messages ────────────────────────────────────────────────────

export async function addMessage(
  ticketId: string,
  data: { authorId: string; authorName: string; authorRole: string; body: string; attachments?: TicketAttachment[] }
): Promise<void> {
  await addDoc(COL.messages(ticketId), {
    ...data,
    attachments: data.attachments ?? [],
    createdAt: serverTimestamp(),
  });
  await updateDoc(doc(COL.tickets(), ticketId), { updatedAt: serverTimestamp() });
}

export function subscribeToMessages(
  ticketId: string,
  cb: (messages: TicketMessage[]) => void
) {
  const q = query(COL.messages(ticketId), orderBy("createdAt", "asc"));
  return onSnapshot(q, (snap) => {
    cb(snap.docs.map((d) => ({ id: d.id, ...d.data() } as TicketMessage)));
  });
}

// ─── Users ───────────────────────────────────────────────────────

export async function getUser(uid: string): Promise<HelpDeskUser | null> {
  const snap = await getDoc(doc(COL.users(), uid));
  return snap.exists() ? (snap.data() as HelpDeskUser) : null;
}

export async function getAgents(): Promise<HelpDeskUser[]> {
  const q = query(COL.users(), where("role", "in", ["agent", "admin"]), where("status", "==", "active"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data() as HelpDeskUser);
}

export async function getActiveUsers(): Promise<HelpDeskUser[]> {
  const q = query(COL.users(), where("status", "==", "active"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data() as HelpDeskUser);
}

export async function updateUser(uid: string, data: Partial<HelpDeskUser>): Promise<void> {
  await updateDoc(doc(COL.users(), uid), { ...data, updatedAt: serverTimestamp() });
}

export async function markNotificationsRead(uid: string): Promise<void> {
  await updateDoc(doc(COL.users(), uid), {
    notificationsLastReadAt: serverTimestamp(),
  });
}

// ─── Self-service profile ────────────────────────────────────────
//
// Firestore rules let a user write only displayName, avatarUrl,
// notifications, notificationsLastReadAt, and updatedAt on their OWN doc
// (see firestore.rules → /users/{userId}). Anything else — role, email,
// department, status — is admin-only and must not be sent from here, or
// the whole update is rejected.

export async function updateMyProfile(
  uid: string,
  patch: { displayName?: string; avatarUrl?: string },
): Promise<void> {
  await updateDoc(doc(COL.users(), uid), { ...patch, updatedAt: serverTimestamp() });
}

export async function updateMyNotifications(
  uid: string,
  prefs: NotificationPreferences,
): Promise<void> {
  await updateDoc(doc(COL.users(), uid), { notifications: prefs, updatedAt: serverTimestamp() });
}

/** Upload a profile photo and return its download URL. The path must be
 *  keyed by uid — Storage rules use that segment as the auth boundary. */
export async function uploadAvatar(uid: string, file: File): Promise<string> {
  const ext = (file.name.split(".").pop() || "jpg").replace(/[^\w]/g, "").slice(0, 8);
  const path = `avatars/${uid}/${Date.now()}.${ext}`;
  const ref = storageRef(storage, path);
  await uploadBytes(ref, file);
  return getDownloadURL(ref);
}

export async function assignTicket(
  ticketId: string,
  agent: { uid: string; displayName: string } | null
): Promise<void> {
  await updateDoc(doc(COL.tickets(), ticketId), {
    assignedTo: agent?.uid ?? null,
    assignedToName: agent?.displayName ?? null,
    updatedAt: serverTimestamp(),
  });
}

export async function changeSubmitter(
  ticketId: string,
  user: { uid: string; email: string; displayName: string }
): Promise<void> {
  await updateDoc(doc(COL.tickets(), ticketId), {
    submitterId: user.uid,
    submitterEmail: user.email,
    submitterName: user.displayName,
    updatedAt: serverTimestamp(),
  });
}

// ─── Attachments ─────────────────────────────────────────────────

export async function uploadTicketAttachment(
  ticketId: string,
  file: File,
  uploadedBy: string
): Promise<TicketAttachment> {
  const path = `attachments/${ticketId}/${Date.now()}_${file.name.replace(/[^\w.\-]/g, "_")}`;
  const ref = storageRef(storage, path);
  await uploadBytes(ref, file);
  const url = await getDownloadURL(ref);
  return {
    name: file.name,
    url,
    storagePath: path,
    contentType: file.type || "application/octet-stream",
    size: file.size,
    uploadedBy,
    uploadedAt: Timestamp.now(),
  };
}

export async function attachToTicket(ticketId: string, attachment: TicketAttachment): Promise<void> {
  await updateDoc(doc(COL.tickets(), ticketId), {
    attachments: arrayUnion(attachment),
    updatedAt: serverTimestamp(),
  });
}

export async function deleteAttachment(attachment: TicketAttachment): Promise<void> {
  try { await deleteObject(storageRef(storage, attachment.storagePath)); }
  catch { /* file may already be gone */ }
}

// ─── Deflections ──────────────────────────────────────────────────

export async function createDeflection(input: {
  userId: string;
  userEmail: string;
  userDisplayName: string;
  issueTitle: string;
  issueDescription: string;
  channel: DeflectionChannel;
  channelTarget?: string;
}): Promise<string> {
  const ref = await addDoc(collection(db, "deflections"), {
    ...input,
    channelTarget: input.channelTarget ?? null,
    status: "candidate",
    startedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function getActiveDeflection(userId: string): Promise<Deflection | null> {
  const q = query(
    collection(db, "deflections"),
    where("userId", "==", userId),
    where("status", "==", "candidate"),
    orderBy("startedAt", "desc"),
    limit(1),
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() } as Deflection;
}

export async function confirmDeflection(deflectionId: string): Promise<void> {
  await updateDoc(doc(db, "deflections", deflectionId), {
    status: "resolved",
    resolvedAt: serverTimestamp(),
  });
}

export async function abandonDeflection(deflectionId: string): Promise<void> {
  await updateDoc(doc(db, "deflections", deflectionId), {
    status: "abandoned",
    abandonedAt: serverTimestamp(),
  });
}

// ─── Knowledge Base ───────────────────────────────────────────────

export async function getKBArticles(category?: string): Promise<KBArticle[]> {
  const constraints: QueryConstraint[] = [orderBy("viewCount", "desc"), limit(50)];
  if (category) constraints.unshift(where("category", "==", category));
  const snap = await getDocs(query(COL.kb(), ...constraints));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as KBArticle));
}

export async function incrementKBView(articleId: string): Promise<void> {
  const ref = doc(COL.kb(), articleId);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    await updateDoc(ref, { viewCount: (snap.data().viewCount ?? 0) + 1 });
  }
}

export async function getKBArticle(articleId: string): Promise<KBArticle | null> {
  const snap = await getDoc(doc(COL.kb(), articleId));
  return snap.exists() ? ({ id: snap.id, ...snap.data() } as KBArticle) : null;
}

export async function createKBArticle(data: {
  title: string; body: string; category: string;
  platforms?: import("@/types").Platform[];
}): Promise<string> {
  const ref = await addDoc(COL.kb(), {
    title: data.title,
    body: data.body,
    category: data.category,
    platforms: data.platforms ?? [],
    viewCount: 0,
    rating: 0,
    ratingCount: 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateKBArticle(articleId: string, data: {
  title?: string; body?: string; category?: string;
  platforms?: import("@/types").Platform[];
}): Promise<void> {
  await updateDoc(doc(COL.kb(), articleId), { ...data, updatedAt: serverTimestamp() });
}

export async function deleteKBArticle(articleId: string): Promise<void> {
  await deleteDoc(doc(COL.kb(), articleId));
}

// ─── Sync Logs ───────────────────────────────────────────────────

export async function getSyncLogs(limitN = 10): Promise<SyncLog[]> {
  const q = query(COL.syncLogs(), orderBy("runAt", "desc"), limit(limitN));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as SyncLog));
}

// ─── Config ──────────────────────────────────────────────────────

export async function getConfigDoc<T>(docId: string): Promise<T | null> {
  const snap = await getDoc(doc(COL.config(), docId));
  return snap.exists() ? (snap.data() as T) : null;
}

export async function setConfigDoc<T extends object>(docId: string, data: T): Promise<void> {
  await setDoc(doc(COL.config(), docId), { ...data, updatedAt: serverTimestamp() }, { merge: true });
}

// ─── Analytics ───────────────────────────────────────────────────

export async function getTicketStats(): Promise<{
  open: number; inProgress: number; resolvedToday: number; critical: number;
}> {
  const startOfDay = new Date(); startOfDay.setHours(0, 0, 0, 0);

  // Note: the "resolved today" query is a single inequality on resolvedAt
  // (not status + resolvedAt) so no composite index is required.
  // A ticket that was resolved today and later reopened still counts —
  // the resolution event still happened today.
  const [openSnap, inProgSnap, critSnap, resolvedSnap] = await Promise.all([
    getDocs(query(COL.tickets(), where("status", "==", "open"))),
    getDocs(query(COL.tickets(), where("status", "==", "in_progress"))),
    getDocs(query(COL.tickets(), where("status", "==", "open"), where("priority", "==", "P1"))),
    getDocs(query(COL.tickets(), where("resolvedAt", ">=", Timestamp.fromDate(startOfDay)))),
  ]);

  return {
    open: openSnap.size,
    inProgress: inProgSnap.size,
    resolvedToday: resolvedSnap.size,
    critical: critSnap.size,
  };
}

export interface DashboardOpsStats {
  avgResolutionMs: number | null;
  slaCompliancePct: number | null;
  avgOpenAgeMs: number | null;
  totalLast30: number;
  resolvedLast30: number;
}

export async function getDashboardOpsStats(): Promise<DashboardOpsStats> {
  const since = Timestamp.fromDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));

  const [recentSnap, openSnap] = await Promise.all([
    getDocs(query(COL.tickets(), where("createdAt", ">=", since))),
    getDocs(query(COL.tickets(), where("status", "in", ["open", "in_progress"]))),
  ]);

  let resolutionTotalMs = 0;
  let resolutionCount = 0;
  let slaOnTime = 0;
  let slaTotal = 0;
  const now = Date.now();

  for (const d of recentSnap.docs) {
    const t = d.data() as Ticket;
    if (t.resolvedAt && t.createdAt) {
      resolutionTotalMs += t.resolvedAt.toMillis() - t.createdAt.toMillis();
      resolutionCount++;
    }
    if (t.priority && t.slaBreach) {
      const breachMs = t.slaBreach.toMillis();
      const resolvedMs = t.resolvedAt?.toMillis();
      if (resolvedMs) {
        slaTotal++;
        if (resolvedMs <= breachMs) slaOnTime++;
      } else if (now > breachMs) {
        slaTotal++;
      }
    }
  }

  let openAgeTotal = 0;
  let openAgeCount = 0;
  for (const d of openSnap.docs) {
    const t = d.data() as Ticket;
    if (!t.createdAt) continue;
    openAgeTotal += now - t.createdAt.toMillis();
    openAgeCount++;
  }

  return {
    avgResolutionMs: resolutionCount > 0 ? resolutionTotalMs / resolutionCount : null,
    slaCompliancePct: slaTotal > 0 ? Math.round((slaOnTime / slaTotal) * 100) : null,
    avgOpenAgeMs: openAgeCount > 0 ? openAgeTotal / openAgeCount : null,
    totalLast30: recentSnap.size,
    resolvedLast30: resolutionCount,
  };
}

export interface DeflectionStats {
  resolvedLast30: number;
  byChannel: { kb: number; aiChat: number };
  ticketsCreatedLast30: number;
  deflectionRate: number | null; // % of "issues" deflected (resolved / (resolved + tickets))
}

export async function getDeflectionStats(): Promise<DeflectionStats> {
  const since = Timestamp.fromDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));

  const [defSnap, ticketSnap] = await Promise.all([
    getDocs(query(
      collection(db, "deflections"),
      where("status", "==", "resolved"),
      where("resolvedAt", ">=", since),
    )),
    getDocs(query(COL.tickets(), where("createdAt", ">=", since))),
  ]);

  let kb = 0, aiChat = 0;
  for (const d of defSnap.docs) {
    const ch = d.data().channel;
    if (ch === "kb") kb++;
    else if (ch === "ai-chat") aiChat++;
  }

  const resolved = defSnap.size;
  const tickets = ticketSnap.size;
  const total = resolved + tickets;
  return {
    resolvedLast30: resolved,
    byChannel: { kb, aiChat },
    ticketsCreatedLast30: tickets,
    deflectionRate: total > 0 ? Math.round((resolved / total) * 100) : null,
  };
}

// Stream of recent resolved deflections — used by the Analytics page to
// show recent activity with username and original issue text.
export async function getRecentResolvedDeflections(limitN = 25): Promise<Deflection[]> {
  const q = query(
    collection(db, "deflections"),
    where("status", "==", "resolved"),
    orderBy("resolvedAt", "desc"),
    limit(limitN),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as Deflection));
}
